/**
 * Skills — the pane's rules and its client, item BD.
 *
 * The rules are pure and live here rather than in the pane for the reason
 * `selectedInstance.ts` gives: `canRunSkill` decides **which document a skill's
 * code is sent to**, and a rule that only exists inside a button's `disabled`
 * prop is a rule no test can hold to.
 *
 * The client talks to the **local agent** (`/skills`, `/skill-folders`), not
 * api-v2 — the store is on this machine. Every call carries the bearer and the
 * organisation, because the agent's `requireAction` gates answer from api-v2's
 * `/user/me/permissions` for that pair.
 *
 * Shapes follow `bimgo-ai-web`'s `ToolsList.tsx` (`ApiTool` → `Tool`) so the
 * cards and the detail port with their field names intact.
 */
import type { SkillJournalRecord } from "./skillEditJournal";
import type { CadInstance } from "../bimgo";
import { agentClient, type AgentClient } from "./agentClient";
import { parseAskError, type ParsedAskError } from "./askError";
import type { Selection } from "./selectedInstance";
import { bearer } from "./accessToken";

// --- types ------------------------------------------------------------------------

export type HostKind = "revit" | "autocad";

export interface SkillHost {
  kind: HostKind;
  /** The product year; empty when the skill does not say. */
  version: string;
}

/** The reference's `ParameterSchemaItem`. */
export interface ParameterSchemaItem {
  name: string;
  displayName?: string | null;
  type: string;
  description?: string | null;
  required?: boolean;
  default?: unknown;
  /** Should be an array; persisted skills may carry `"A;B;C"`. Always guard. */
  enum?: unknown;
  minimum?: number;
  maximum?: number;
  [key: string]: unknown;
}

/** What `GET /skills` returns per skill — the agent's `ListedSkill`. */
export interface ApiSkill {
  id: string;
  folderName: string;
  parentFolder: string | null;
  target: SkillHost;
  /** An AutoCAD skill's year folders — every row of it lists them all. */
  versionFolders?: string[];
  artifact: {
    name: string;
    displayName?: string;
    description: string;
    language: string;
    executionAt: string;
    revitVersion?: string;
    parameterSchema?: ParameterSchemaItem[];
    autoTransaction?: "auto" | boolean | string;
    enabled?: boolean;
    lastUsedParameters?: Record<string, unknown>;
    lastUsedAt?: string;
    tags?: string[];
  };
  versioning: {
    current_version: number;
    published_version: number | null;
    cloud_id: string | null;
    downloaded_version?: number | null;
    downloaded_at?: string | null;
    publisher_id?: string | null;
  };
}

/** The reference's `Tool`, flattened for the cards. */
export interface Skill {
  /** The app's key for this row: the id qualified by its year (`skillKey`). */
  id: string;
  /** The agent's id, without the year — `autocad:<uuid>` for an AutoCAD skill. */
  baseId: string;
  /**
   * An AutoCAD skill's year folders (docs/proposals/workspaces.md §2): one skill,
   * one history, a folder per AutoCAD year. Absent for a Revit skill, whose
   * year is the store it sits in.
   */
  versionFolders?: string[];
  name: string;
  displayName: string;
  description: string;
  language: string;
  createdAt: string;
  target: SkillHost;
  parameterSchema?: ParameterSchemaItem[];
  autoTransaction?: "auto" | boolean;
  isPublished: boolean;
  currentVersion: number;
  enabled: boolean;
  lastUsedParameters?: Record<string, unknown>;
  parentFolder: string | null;
  /** The cloud skill this local copy tracks — the Organization tab's "Saved" gate. */
  cloudId: string | null;
  /** The cloud version last downloaded; null for a skill never synced. */
  downloadedVersion: number | null;
  /**
   * Who owns the cloud copy this one tracks, when it came down from someone else's publish.
   *
   * Written by the agent's sync from the cloud row's `owner_id`, and left null for a skill that
   * has only ever been published from here — so null means "nobody else's", not "unknown".
   * `canPublishSkill` is the one reader.
   */
  publisherId: string | null;
}

/**
 * One skill as api-v2 shares it — `GET /skills/cloud/shared` through the agent.
 * Field names are api-v2's own (`display_name`, `owner_id`, `published_version`).
 */
export interface CloudSkill {
  id: string;
  local_id?: string | null;
  name: string;
  display_name?: string | null;
  description: string;
  metadata?: {
    language?: string;
    revitVersion?: string;
    parameterSchema?: ParameterSchemaItem[];
    tags?: string[];
    [key: string]: unknown;
  };
  published_version: number;
  visibility: string;
  owner_id: string;
  owner_email?: string | null;
  created_at: string;
  updated_at: string;
}

/** The cloud ids the local store already tracks — what turns Download into "Saved". */
export function savedCloudIds(skills: readonly Pick<Skill, "cloudId">[]): Set<string> {
  return new Set(skills.map((s) => s.cloudId).filter((id): id is string => typeof id === "string" && id.length > 0));
}

/** The reference's `filteredSharedTools`: name or description, case-insensitive. */
export function filterCloudSkills(skills: readonly CloudSkill[], query: string): CloudSkill[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...skills];
  return skills.filter(
    (s) => (s.display_name || s.name || "").toLowerCase().includes(q) || (s.description || "").toLowerCase().includes(q),
  );
}

export interface SkillFolder {
  name: string;
  skillCount: number;
  /** The Revit year whose store the folder sits in — folders are per year, as in 2.x. */
  version: string;
  /**
   * The store the folder is in. Absent means Revit; an AutoCAD folder is
   * vendor-wide and its `version` is empty, because the year is inside each skill.
   */
  kind?: HostKind;
}

export interface SkillDetail extends Skill {
  code: string;
  skillContent: string;
}

export interface SkillVersion {
  version: number;
  name: string;
  description: string;
  saved_at: string | null;
  message: string;
  sha: string;
}

export interface SkillVersionsPage {
  current_version: number;
  published_version: number | null;
  versions: SkillVersion[];
}

export interface SkillVersionFiles {
  version: number;
  code: string;
  skillContent: string;
}

export interface ExecutionSummary {
  executionId: string;
  executedAt: string;
  success: boolean;
  durationMs: number | null;
  timedOut: boolean;
  errorMessage: string;
  skillVersion: number | null;
  revitVersion: string | null;
}

/** One recorded run in full — the agent's `SkillExecutionDoc`. */
export interface ExecutionDoc extends ExecutionSummary {
  parameters?: unknown[];
  parametersByName?: Record<string, unknown> | null;
  result: unknown;
  display?: unknown;
}

/** The reference's `DisplayPayload`, kept opaque: `ExecutionDisplayView` reads it. */
export interface ExecutionResult {
  success: boolean;
  result: unknown;
  errorMessage: string;
  execution: { executionId: string; durationMs: number; timedOut: boolean; timeoutMs: number };
  display?: unknown;
  executionAt: string;
}

export function toSkill(api: ApiSkill): Skill {
  const a = api.artifact;
  const auto = a.autoTransaction;
  const target = skillTarget({ host: api.target, revitVersion: a.revitVersion });
  return {
    id: skillKey(api.id, target.version),
    baseId: api.id,
    ...(Array.isArray(api.versionFolders) ? { versionFolders: api.versionFolders } : {}),
    name: a.name,
    displayName: a.displayName || a.name,
    description: a.description ?? "",
    language: a.language ?? "csharp",
    createdAt: a.executionAt,
    target,
    parameterSchema: Array.isArray(a.parameterSchema) ? a.parameterSchema : undefined,
    autoTransaction: auto === "auto" || typeof auto === "boolean" ? auto : undefined,
    isPublished: api.versioning?.published_version != null,
    currentVersion: api.versioning?.current_version ?? 1,
    enabled: a.enabled !== false,
    lastUsedParameters: a.lastUsedParameters,
    parentFolder: api.parentFolder ?? null,
    cloudId: api.versioning?.cloud_id ?? null,
    downloadedVersion: api.versioning?.downloaded_version ?? null,
    publisherId: api.versioning?.publisher_id ?? null,
  };
}

// --- the key -------------------------------------------------------------------------

/**
 * A skill's key in this app: its id qualified by its year, `uuid@2024`.
 *
 * The id alone does not name one skill. 2.x's cross-version deploy writes the
 * target year's copy under the source's id, so the list holds one id twice,
 * one row per year (#189). Keyed by id, the agent resolved either row to the
 * oldest year's copy, so the newer copy was refused on its own Revit, and
 * editing or deleting it touched the other copy. Every map, React key and edit
 * session keys on this, and `skillPath` splits it back apart for the wire.
 */
export function skillKey(id: string, version: string): string {
  return version ? `${id}@${version}` : id;
}

/** The agent's route for a skill key: the bare id in the path, the year as `?version=`. */
export function skillPath(key: string, rest = ""): string {
  const match = /^(.*)@(\d{4})$/.exec(key);
  const id = match ? match[1]! : key;
  const path = `${skillsBase(id)}/${encodeURIComponent(id)}${rest}`;
  return match ? `${path}${rest.includes("?") ? "&" : "?"}version=${match[2]}` : path;
}

/**
 * Which generation's routes a skill id belongs to (docs/proposals/workspaces.md): a
 * **skill v2** — one skill, a code folder per vendor version, one git history; AutoCAD
 * skills, `autocad:<uuid>` — lives at `/v2/skills`, a v1 Revit skill at `/skills`. The
 * views are shared; only the routes differ, and v1's retire after Revit migrates.
 */
export function skillsBase(id: string): "/v2/skills" | "/skills" {
  return id.startsWith("autocad:") ? "/v2/skills" : "/skills";
}

// --- the target ----------------------------------------------------------------------

export const HOST_KINDS: readonly HostKind[] = ["revit", "autocad"];

export function isHostKind(value: unknown): value is HostKind {
  return typeof value === "string" && (HOST_KINDS as readonly string[]).includes(value);
}

/**
 * The same derivation as the agent's `skillTarget`, for the rare payload that
 * arrives without one (an older agent). The explicit field wins; a skill with
 * only `revitVersion` is a Revit skill; nothing at all is Revit of unknown year.
 */
export function skillTarget(artifact: { host?: Partial<SkillHost> | null; revitVersion?: string | null }): SkillHost {
  const host = artifact.host;
  if (host && isHostKind(host.kind)) return { kind: host.kind, version: trim(host.version) };
  return { kind: "revit", version: trim(artifact.revitVersion) };
}

function trim(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export const VENDOR_NAMES: Record<HostKind, string> = { revit: "Revit", autocad: "AutoCAD" };

export function describeTarget(target: SkillHost, unknownLabel = "unknown version"): string {
  const name = VENDOR_NAMES[target.kind] ?? target.kind;
  return target.version ? `${name} ${target.version}` : `${name} · ${unknownLabel}`;
}

/**
 * An instance's product year: `hostVersion` (any vendor), else 2.x's
 * `revitVersion`. AutoCAD manifests carry only the first.
 */
export function instanceYear(instance: Pick<CadInstance, "hostVersion" | "revitVersion"> | null | undefined): string {
  return trim(instance?.hostVersion ?? instance?.revitVersion);
}

/** Does a running instance satisfy a skill's target? Exact on both axes. */
export function hostMatches(target: SkillHost, instance: Pick<CadInstance, "host_kind" | "hostVersion" | "revitVersion">): boolean {
  return target.version !== "" && instance.host_kind === target.kind && instanceYear(instance) === target.version;
}

// --- grouping ----------------------------------------------------------------------------

export interface VersionGroup {
  /** The year, or `""` for skills that do not say. */
  version: string;
  skills: Skill[];
  /** A running instance of this vendor and year exists. */
  connected: boolean;
}

export interface VendorGroup {
  kind: HostKind;
  versions: VersionGroup[];
}

/**
 * Vendor → version, versions ascending, a version present when it has a skill
 * **or** a running instance — so a user with Revit 2025 open and no 2025 skills
 * sees where those would go. Unknown-year skills come last under their vendor.
 * A vendor with nothing at all is omitted; the rail is not a catalogue.
 */
/**
 * The vendor and year a cloud skill was written for — the same axis as a local
 * skill's target, read from the cloud metadata 2.x wrote (`revitVersion`).
 * Cloud skills are Revit skills today; a skill that does not say its year is
 * `{ revit, "" }` and lists under the vendor's unknown-version node.
 */
/**
 * A cloud skill's target, as far as the cloud can say.
 *
 * **The vendor is read from `metadata.host` when it is there, and assumed Revit when it
 * is not** — which today is always, because publish does not send it. That assumption
 * used to be hardcoded here, and it is why the Organization tab is empty for any vendor
 * but Revit: the filter asks for AutoCAD and every row claims Revit.
 *
 * Reading the field costs nothing and does nothing until publish writes it. `metadata`
 * is a free-form JSONB column in api-v2 that round-trips verbatim, so carrying the
 * vendor needs no schema change there — only the publish and download sides, which are
 * out of scope here.
 */
export function cloudSkillTarget(cloud: Pick<CloudSkill, "metadata">): SkillHost {
  const host = cloud.metadata?.["host"];
  const kind =
    host && typeof host === "object" && isHostKind((host as { kind?: unknown }).kind)
      ? ((host as { kind: HostKind }).kind)
      : "revit";
  const version =
    host && typeof host === "object" ? trim((host as { version?: unknown }).version) : "";
  return { kind, version: version || trim(cloud.metadata?.revitVersion) };
}

/**
 * The organization's skills for one vendor — every version of it.
 *
 * Was per vendor *and* version. The version is a card badge now, not a place, so
 * filtering it out here would hide a shared skill from the only list that shows it.
 */
export function cloudSkillsForVendor(skills: readonly CloudSkill[], pick: VendorPick | null): CloudSkill[] {
  if (!pick) return [];
  return skills.filter((s) => cloudSkillTarget(s).kind === pick.kind);
}

export function groupSkills(
  skills: readonly Skill[],
  instances: readonly CadInstance[],
  /**
   * Targets that must have a node even with no local skill and no running
   * instance — the organization's skills' years, so a year you have nothing
   * for locally is still browsable for what the org shares (item BG).
   */
  extraTargets: readonly SkillHost[] = [],
): VendorGroup[] {
  const byKind = new Map<HostKind, Map<string, VersionGroup>>();
  const ensure = (kind: HostKind, version: string): VersionGroup => {
    let versions = byKind.get(kind);
    if (!versions) {
      versions = new Map();
      byKind.set(kind, versions);
    }
    let group = versions.get(version);
    if (!group) {
      group = { version, skills: [], connected: false };
      versions.set(version, group);
    }
    return group;
  };

  for (const skill of skills) ensure(skill.target.kind, skill.target.version).skills.push(skill);
  for (const target of extraTargets) ensure(target.kind, target.version);
  for (const instance of instances) {
    if (!isHostKind(instance.host_kind)) continue;
    const version = instanceYear(instance);
    if (!version) continue;
    ensure(instance.host_kind, version).connected = true;
  }

  return HOST_KINDS.filter((kind) => byKind.has(kind)).map((kind) => ({
    kind,
    versions: [...byKind.get(kind)!.values()].sort(compareVersions),
  }));
}

function compareVersions(a: VersionGroup, b: VersionGroup): number {
  if (a.version === "") return 1;
  if (b.version === "") return -1;
  return a.version.localeCompare(b.version, undefined, { numeric: true });
}

/**
 * What the tree selects: **a vendor service**, not a vendor/version pair.
 *
 * The version was a navigation level because 2.x only ever served Revit, so the year
 * was the only axis worth splitting on. With a second vendor that reading inverts: the
 * thing you switch between is Revit and AutoCAD, and the year is a property of a skill
 * — which is why the card has always shown it (`describeTarget`). Nesting it kept a
 * user who works across two years hopping between nodes to find one skill.
 *
 * The version has not stopped mattering; it stopped being a *place*. Each skill still
 * carries its own `target`, the run gate still demands an exact vendor and year match,
 * and the card still names it.
 */
export interface VendorPick {
  kind: HostKind;
}

/** Every skill the vendor has, across all of its versions, newest first. */
export function skillsForVendor(groups: readonly VendorGroup[], pick: VendorPick | null): Skill[] {
  if (!pick) return [];
  const group = groups.find((g) => g.kind === pick.kind);
  if (!group) return [];
  return group.versions.flatMap((v) => v.skills);
}

/** The versions this vendor has anything under — the card's badge values, and what "New folder" may target. */
export function versionsForVendor(groups: readonly VendorGroup[], pick: VendorPick | null): string[] {
  if (!pick) return [];
  const group = groups.find((g) => g.kind === pick.kind);
  if (!group) return [];
  return group.versions.map((v) => v.version).filter((v) => v !== "");
}

/**
 * The vendor's years that have a running instance right now.
 *
 * The version filter marks these, which is where the "connected" signal moved to: it was a
 * dot on the vendor, and a dot saying "*something* of this vendor is running" answers a
 * question nobody asks — what matters is whether the year a skill targets is open, because
 * that is what the run gate checks (`hostMatches`, exact on both axes).
 */
export function connectedVersionsForVendor(groups: readonly VendorGroup[], pick: VendorPick | null): string[] {
  if (!pick) return [];
  const group = groups.find((g) => g.kind === pick.kind);
  if (!group) return [];
  return group.versions.filter((v) => v.connected && v.version !== "").map((v) => v.version);
}

/** Is any instance of this vendor running? */
export function vendorConnected(groups: readonly VendorGroup[], kind: HostKind): boolean {
  return groups.find((g) => g.kind === kind)?.versions.some((v) => v.connected) ?? false;
}

/** How many skills the vendor holds, for the tree's count. */
export function vendorSkillCount(groups: readonly VendorGroup[], kind: HostKind): number {
  return groups.find((g) => g.kind === kind)?.versions.reduce((n, v) => n + v.skills.length, 0) ?? 0;
}

/**
 * The year the Skills pane is scoped to — **the selected instance's, or none.**
 *
 * The scope used to be a filter of its own, which meant the pane could be looking at 2021
 * while the status bar was driving a 2024 document. One source now (owner, 2026-09-20): the
 * instance picked in the footer decides the year, and everything the pane shows — My
 * Skills, Folders and the organization fetch — is narrowed to it.
 *
 * Empty when nothing is running, or when the running instance reports no year, which is the
 * same `ALL_VERSIONS` sentinel `atVersion` reads as "do not narrow". The pane does not show
 * a list in that state at all — it asks for Revit to be opened — so the empty string is a
 * resting value rather than a fallback anyone browses under.
 *
 * A `gone` selection (the picked instance closed while others run) yields nothing on
 * purpose, for the reason `resolveSelectedInstance` gives: a choice that has closed is never
 * silently replaced, and scoping the pane to some other Revit's year would do exactly that.
 */
export function scopeVersion(selection: Selection): string {
  return instanceYear(selection.instance);
}

/**
 * Where the tree lands: the selected instance's vendor when the tree has it, else the
 * first vendor. The skills you can run right now come first, as before.
 */
export function defaultVendor(groups: readonly VendorGroup[], selection: Selection): VendorPick | null {
  const instance = selection.instance;
  if (instance && groups.some((g) => g.kind === instance.host_kind)) {
    return { kind: instance.host_kind as HostKind };
  }
  const first = groups[0];
  return first ? { kind: first.kind } : null;
}

/**
 * The vendor's folders for the years in scope — **one card per directory** (owner).
 *
 * An earlier pass merged them by name: "Export" in 2024 and 2025 became one row with the
 * counts added. That was wrong in the way that matters, because it claimed a folder that
 * does not exist. On disk they are two directories, a skill can only be moved into the one
 * in its own year, and `restore`, `publish` and the git history all belong to one of them.
 * A single card implied an identity nothing downstream honours.
 *
 * So two directories are two cards, each naming its year. The `versions` argument is the
 * scope: every year the vendor holds, or the one the filter has narrowed to.
 *
 * Sorted by name, then year, so the pair sit together rather than at opposite ends of the
 * grid — which is the one thing the merge got right.
 */
export function foldersForVendor(
  folders: readonly SkillFolder[],
  versions: readonly string[],
): SkillFolder[] {
  const inScope = new Set(versions);
  return folders
    .filter((folder) => inScope.has(folder.version))
    .sort((a, b) => a.name.localeCompare(b.name) || a.version.localeCompare(b.version, undefined, { numeric: true }));
}

/** A folder is identified by its name **and** its year — there is one directory per pair. */
export interface FolderPick {
  name: string;
  version: string;
}

/** Is this the folder that is open? Both halves, or 2024's "Export" opens 2025's too. */
export function isFolderPick(folder: SkillFolder, pick: FolderPick | null): boolean {
  return pick !== null && folder.name === pick.name && folder.version === pick.version;
}

/**
 * Narrow a list to one product year, or leave it whole.
 *
 * Here rather than inline in the pane for the reason this file's header gives: the year
 * decides **which document a skill's code can be sent to**, so "is this skill in scope"
 * is a rule, and a rule that exists only inside a `useMemo` is one no test can hold to.
 * Both tabs filter through this — the saved list on `skill.target.version`, the shared
 * one on `cloudSkillTarget(...).version`.
 *
 * An empty `version` means every year, which needs no sentinel: no skill has "" as a
 * year it targets, and one that reports no year at all is precisely what the unfiltered
 * list should still show.
 */
export function atVersion<T>(items: readonly T[], version: string, versionOf: (item: T) => string): T[] {
  if (version === "") return [...items];
  return items.filter((item) => versionOf(item) === version);
}

/** The distinct years present in a list, ascending — what the filter offers. */
export function versionsPresent<T>(items: readonly T[], versionOf: (item: T) => string): string[] {
  const seen = new Set(items.map(versionOf).filter((v) => v !== ""));
  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/**
 * How a skills list is ordered — **the date direction only.**
 *
 * The version is not one of the choices: it is always the *primary* key, ascending
 * (owner). Skills group by product year low to high, because a version is a scale and a
 * scale reads that way, and the choice here is how the skills *inside* a year are
 * ordered. So this names a direction, not a key.
 *
 * That also removes an asymmetry an earlier shape had. An `updated` option would work on
 * the cloud list, which records `updated_at`, and quietly fall back to creation on the
 * local one, whose only timestamp is `artifact.executionAt` — written once by
 * `createSkill` and never touched, since saving a version bumps a counter and writes a
 * commit, neither of which lands in the artifact. One date axis, both lists honest.
 */
export type SkillSort = "newest" | "oldest";

/** Newest within each version — what a list of work in progress should open on. */
export const DEFAULT_SKILL_SORT: SkillSort = "newest";

export interface SortKeys<T> {
  version: (item: T) => string;
  /** When it came into being: created locally, published for a shared one. */
  created: (item: T) => string;
  /** The tie-break, so equal keys do not reorder between renders. */
  name: (item: T) => string;
}

function timeOf(raw: string): number {
  const at = Date.parse(raw);
  return Number.isNaN(at) ? 0 : at;
}

/**
 * Sort a copy: version ascending, then the chosen date direction, then name.
 *
 * A tie-break is not decoration. Without one, two skills created in the same second swap
 * places on each render, which reads as the list flickering.
 *
 * A skill with **no** year sorts last rather than first — "unknown" is not a low version,
 * and leading an ascending list with it would be worse than trailing. An unparseable date
 * counts as the oldest, which puts it last under `newest` and first under `oldest`; either
 * way it stays visible rather than being dropped.
 */
export function sortSkills<T>(items: readonly T[], sort: SkillSort, keys: SortKeys<T>): T[] {
  const oldestFirst = sort === "oldest";
  return [...items].sort((a, b) => {
    const av = keys.version(a);
    const bv = keys.version(b);
    if (av !== bv) {
      if (av === "") return 1;
      if (bv === "") return -1;
      return av.localeCompare(bv, undefined, { numeric: true });
    }
    const newer = timeOf(keys.created(b)) - timeOf(keys.created(a));
    return (oldestFirst ? -newer : newer) || keys.name(a).localeCompare(keys.name(b));
  });
}

/** The reference's search: display name or description, case-insensitive, trimmed. */
export function filterSkills(skills: readonly Skill[], query: string): Skill[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...skills];
  return skills.filter(
    (skill) => skill.displayName.toLowerCase().includes(q) || skill.description.toLowerCase().includes(q),
  );
}

// --- paging ----------------------------------------------------------------------------------

/**
 * Cards per page in the skills list, and what a reader may change it to.
 *
 * **Twenty, up from six** (owner). Six was three rows of a two-column grid in a pane that
 * also carried a vendor tree down the left and a row of folder cards above the skills; with
 * both of those gone the grid has the pane's full width and height, and six cards left the
 * page half empty on anything but a laptop.
 *
 * The choice is offered rather than guessed, because the right number depends on the window
 * and on what the reader is doing: 10 to work through a folder card by card, 50 to scan a
 * large store for one name.
 */
export const SKILLS_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS: readonly number[] = [10, 20, 50];

export interface Page<T> {
  /** The rows to render. */
  items: T[];
  /** The page actually shown, which is not always the one asked for — see below. */
  page: number;
  /** At least 1, so "Page 1 of 1" reads correctly for an empty list. */
  pageCount: number;
  total: number;
}

/**
 * One page of a list, **clamped rather than trusted**.
 *
 * The caller holds the page number in state, and every source of that list can shrink
 * underneath it: a search narrows it, a folder is entered, a skill is deleted, the tree
 * moves to another year. Each of those can leave the remembered page past the end, and
 * the symptom is the worst kind — an empty list that looks exactly like "you have no
 * skills", with no hint that the fix is to page back.
 *
 * So the page is clamped here, at the one place that knows the count, instead of each
 * caller resetting it on each of the four triggers and missing one. Callers still reset
 * to page 1 when the *scope* changes, because staying on page 3 of a different folder
 * is disorienting even when page 3 exists.
 */
export function paginate<T>(items: readonly T[], page: number, perPage: number = SKILLS_PAGE_SIZE): Page<T> {
  const size = Math.max(1, Math.floor(perPage));
  const pageCount = Math.max(1, Math.ceil(items.length / size));
  const safe = Math.min(Math.max(1, Math.floor(page) || 1), pageCount);
  const start = (safe - 1) * size;
  return { items: items.slice(start, start + size), page: safe, pageCount, total: items.length };
}

/**
 * **Publish only what is yours in the cloud** (owner).
 *
 * Three cases, and only the middle one is new:
 *
 *   - **Never published** (`cloudId` null): publishing creates the cloud copy, and the publisher
 *     is whoever pressed the button. Allowed.
 *   - **A copy downloaded from someone else** (`publisherId` set to another user): refused.
 *     Publishing does not update their row — the agent sends the local id and no cloud id, so
 *     api-v2 files a *second* cloud skill with the same name, owned by the presser. The
 *     organization then has two, the local copy quietly stops tracking the original, and the
 *     user believes they updated the skill they downloaded.
 *   - **Your own copy, here or downloaded from your own publish** (`publisherId` is you, or null
 *     because this machine published it): allowed, and it is the ordinary "publish v4" case.
 *
 * `publisherId` is only ever written by a sync, from the cloud row's `owner_id`, so a null is
 * the absence of someone else rather than a missing lookup. With no signed-in user id the answer
 * is the safe one: a copy that names a publisher stays unpublishable until we know who is asking.
 */
export function canPublishSkill(skill: Skill, userId: string | null): boolean {
  if (skill.publisherId === null) return true;
  return userId !== null && skill.publisherId === userId;
}

// --- the run gate ----------------------------------------------------------------------------

export type RunBlock =
  /** The caller lacks `skill:run`. */
  | "no-permission"
  /** The skill is switched off. */
  | "disabled"
  /** The skill does not say which year it is for. */
  | "unknown-version"
  /** Nothing is running, or the pick has closed. */
  | "no-instance"
  /** Something is selected, and it is not what the skill was written for. */
  | "wrong-instance";

export type RunVerdict = { ok: true; instance: CadInstance } | { ok: false; block: RunBlock };

/**
 * May this skill run right now, and against what?
 *
 * Checked in the order the user can fix them: permission (cannot), the skill
 * itself (edit it), then the selection (one click in the status bar). The
 * agent applies the same instance rule and answers `409 host_mismatch`; this
 * is the copy that greys the button out before the round trip.
 */
export function canRunSkill(
  skill: Pick<Skill, "target" | "enabled">,
  selection: Selection,
  /** `hasAction(perms, "skill:run")` — passed in so this file stays free of React. */
  mayRun: boolean,
): RunVerdict {
  if (!mayRun) return { ok: false, block: "no-permission" };
  if (!skill.enabled) return { ok: false, block: "disabled" };
  if (skill.target.version === "") return { ok: false, block: "unknown-version" };
  if (!selection.instance) return { ok: false, block: "no-instance" };
  if (!hostMatches(skill.target, selection.instance)) return { ok: false, block: "wrong-instance" };
  return { ok: true, instance: selection.instance };
}

/** Why the Edit-with-AI composer is blocked — the instance-side blocks only. */
export type EditBlock = "unknown-version" | "no-instance" | "wrong-instance";

export type EditVerdict = { ok: true; instance: CadInstance } | { ok: false; block: EditBlock };

/**
 * May this skill be edited right now, and against what?
 *
 * The same instance rule as running — the owner's rule from phase 1: run *and
 * edit* only when the target instance is running, because the edit agent tests
 * its changes against that document. What it deliberately does not check:
 * `enabled` (a disabled skill is exactly the one being fixed) and permission
 * (the caller passes that separately — it needs its own wording).
 */
export function canEditSkill(skill: Pick<Skill, "target">, selection: Selection): EditVerdict {
  if (skill.target.version === "") return { ok: false, block: "unknown-version" };
  if (!selection.instance) return { ok: false, block: "no-instance" };
  if (!hostMatches(skill.target, selection.instance)) return { ok: false, block: "wrong-instance" };
  return { ok: true, instance: selection.instance };
}

// --- parameters ------------------------------------------------------------------------------

export function parameterLabel(param: { name: string; displayName?: string | null }): string {
  return (typeof param.displayName === "string" && param.displayName.trim()) || param.name;
}

export function parameterLabels(schema: readonly ParameterSchemaItem[] | undefined): Record<string, string> {
  return Object.fromEntries((schema ?? []).map((param) => [
    param.name, typeof param.displayName === "string" ? param.displayName.trim() : "",
  ]));
}

export function parameterLabelUpdate(
  persistedSchema: readonly ParameterSchemaItem[] | undefined,
  labels: Record<string, string>,
): ParameterSchemaItem[] | undefined {
  const original = parameterLabels(persistedSchema);
  if (!persistedSchema?.some((param) => (labels[param.name] ?? "").trim() !== original[param.name])) return undefined;
  const normalizeId = (value: unknown) => typeof value === "number" && Number.isSafeInteger(value) ? String(value) : value;
  // The run form replaces defaults with last-used values; only persisted metadata is safe to save.
  return persistedSchema.map(({ displayName: _old, ...param }) => {
    // Stored ElementIds may predate the strict string-only request contract.
    if (typeof param.type === "string" && param.type.toLowerCase() === "elementid") {
      if ("default" in param) param.default = normalizeId(param.default);
      if (Array.isArray(param.enum)) param.enum = param.enum.map(normalizeId);
    }
    const label = (labels[param.name] ?? "").trim();
    return label ? { ...param, displayName: label } : param;
  });
}

export type RunParameters = { ok: true; parametersJson: string } | { ok: false; missing: string[]; error?: string };

// Revit keeps its existing conversions because saved scripts own their parameter validation.
export function buildRunParameters(schema: readonly ParameterSchemaItem[] | undefined, hostKind?: HostKind): RunParameters {
  if (!schema || schema.length === 0) return { ok: true, parametersJson: "[]" };

  const blank = (value: unknown): boolean => value === undefined || value === null || String(value).trim() === "";
  const missing = schema.filter((p) => p.required && blank(p.default)).map((p) => p.name);
  if (missing.length > 0) return { ok: false, missing };

  if (hostKind === "autocad") {
    for (const param of schema) {
      const raw = param.default;
      const error = (reason: string): RunParameters => ({ ok: false, missing: [], error: `${parameterLabel(param)}: ${reason}` });
      const constrained = param.enum !== undefined || param.minimum !== undefined || param.maximum !== undefined;
      if (blank(raw) && !constrained) continue;
      if (param.type === "number" || param.type === "integer") {
        const number = blank(raw) ? NaN : Number.parseFloat(String(raw));
        if (!Number.isFinite(number) || number !== Number(raw)) return error("Enter a finite number.");
        if (param.type === "integer" && !Number.isInteger(number)) return error("Enter an integer.");
        if (typeof param.minimum === "number" && number < param.minimum) return error(`Minimum: ${param.minimum}.`);
        if (typeof param.maximum === "number" && number > param.maximum) return error(`Maximum: ${param.maximum}.`);
      }
      if (param.type === "boolean" && !["true", "false"].includes(String(raw).toLowerCase())) {
        return error("Choose true or false.");
      }
      if (param.enum !== undefined && !enumOptions(param).includes(String(raw))) return error("Choose an available option.");
    }
  }

  const values = schema.map((param) => {
    const raw = param.default;
    if (hostKind === "autocad" && param.type === "string" && typeof raw === "string") return raw;
    if (blank(raw)) return param.required ? "" : null;
    if (param.type === "number" || param.type === "integer") {
      const n = Number.parseFloat(String(raw));
      return Number.isNaN(n) ? (param.required ? "" : null) : n;
    }
    if (param.type === "boolean") return String(raw).toLowerCase() === "true";
    return raw;
  });
  return { ok: true, parametersJson: JSON.stringify(values) };
}

/** Start the form from the last run, as the reference's `mergeLastUsedIntoSchema` does. */
export function withLastUsed(
  schema: readonly ParameterSchemaItem[] | undefined,
  lastUsed: Record<string, unknown> | undefined,
): ParameterSchemaItem[] {
  if (!schema) return [];
  return schema.map((param) => {
    const previous = lastUsed?.[param.name];
    return previous === undefined ? { ...param } : { ...param, default: previous };
  });
}

/** The reference's `enum` guard: an array, or `"A;B;C"`, or nothing. */
export function enumOptions(param: ParameterSchemaItem): string[] {
  if (Array.isArray(param.enum)) return param.enum.map(String);
  if (typeof param.enum === "string" && param.enum.trim()) return param.enum.split(";").map((s) => s.trim()).filter(Boolean);
  return [];
}

// --- the client --------------------------------------------------------------------------------

export class SkillsError extends Error {
  constructor(
    readonly parsed: ParsedAskError,
    message: string,
  ) {
    super(message);
    this.name = "SkillsError";
  }
  get status(): number {
    return this.parsed.httpStatus;
  }
  /** The agent's `details.detail`, e.g. `host_mismatch`. */
  get detail(): string | null {
    const d = this.parsed.details?.["detail"];
    return typeof d === "string" ? d : null;
  }
}

export interface SkillsAuth {
  token: string;
  orgId: string;
}

/** One Edit-with-AI conversation in the skill's history (the agent's entry). */
export interface RegenThreadSummary {
  id: string;
  title: string;
  created_at: string;
  /** AutoCAD skills: when the conversation was last continued. */
  last_used_at?: string;
  /** AutoCAD skills: the year folder the conversation last ran against. */
  version_folder?: string | null;
}

/**
 * What the chat's "Save Skill" sends — 2.x's `POST /skills` body: the
 * `send_code_to_revit` artifact plus the year it ran against, which names the
 * store it lands in.
 */
export interface SkillCreateBody {
  /** `autocad` saves into the AutoCAD store, at `version`; absent is Revit's, at `revitVersion`. */
  vendor?: "autocad";
  version?: string;
  name: string;
  displayName?: string;
  description?: string;
  code: string;
  parameterSchema?: ParameterSchemaItem[];
  autoTransaction?: "auto" | boolean | string;
  tags?: string[];
  language?: string;
  revitVersion?: string;
}

/**
 * The vendor and year a send_code artifact ran against: `artifact.host` when it
 * names a known vendor and a year, else 2.x's `revitVersion` as Revit. Null when
 * neither says a year — the artifact cannot be filed under one.
 */
export function artifactTarget(artifact: Record<string, unknown> | undefined): SkillHost | null {
  const host = artifact?.["host"];
  if (isRecord(host) && isHostKind(host["kind"])) {
    const version = trim(host["version"]);
    if (/^\d{4}$/.test(version)) return { kind: host["kind"], version };
  }
  const year = artifactYear(artifact);
  return year ? { kind: "revit", version: year } : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The year a send_code artifact ran against, or null when it does not say. */
export function artifactYear(artifact: Record<string, unknown> | undefined): string | null {
  const raw = artifact?.["revitVersion"];
  return typeof raw === "string" && /^\d{4}$/.test(raw) ? raw : null;
}

/** Fields the editor's Update writes — 2.x's PUT body, the editable subset. */
export interface SkillUpdate {
  displayName?: string;
  description?: string;
  code?: string;
  parameterSchema?: ParameterSchemaItem[];
}

export interface SkillsClient {
  list(): Promise<Skill[]>;
  get(skillId: string): Promise<SkillDetail>;
  folders(): Promise<SkillFolder[]>;
  /** A grouping folder: in one Revit year's store, or vendor-wide in the AutoCAD store. */
  createFolder(name: string, version: string, kind?: HostKind): Promise<SkillFolder>;
  move(skillId: string, folderName: string | null): Promise<void>;
  setEnabled(skillId: string, enabled: boolean): Promise<void>;
  remove(skillId: string): Promise<void>;
  versions(skillId: string): Promise<SkillVersionsPage>;
  version(skillId: string, version: number): Promise<SkillVersionFiles>;
  executions(skillId: string, limit?: number): Promise<ExecutionSummary[]>;
  execution(skillId: string, executionId: string): Promise<ExecutionDoc>;
  execute(skillId: string, body: { targetSessionId: string; parametersJson: string; autoTransaction?: "auto" | boolean }): Promise<ExecutionResult>;
  update(skillId: string, fields: SkillUpdate): Promise<void>;
  /** The chat's Save Skill: `POST /skills` into the named year's store. */
  createSkill(body: SkillCreateBody): Promise<{ id: string; displayName: string }>;
  saveVersion(skillId: string, message?: string): Promise<{ version: number }>;
  /** A checkout, not a commit — the agent's switch semantics (design §9 ph. 2). */
  /**
   * An AutoCAD skill's restore is a checkout of every year folder: `openVersion` is the year
   * to show it at afterwards — the open one, or the newest left if that one did not exist then.
   */
  restore(skillId: string, version: number): Promise<{ version: number; openVersion?: string; versionFolders?: string[] }>;
  regenThreads(skillId: string): Promise<{ threads: RegenThreadSummary[]; current: string | null }>;
  /**
   * An AutoCAD skill conversation's local Work Journal, oldest first: messages, replies, tool
   * calls and results (`lib/skillEditJournal.ts`). Empty for one from before the journal.
   */
  regenThreadJournal(skillId: string, threadId: string): Promise<SkillJournalRecord[]>;
  /** An image that conversation's journal kept, as a Blob: the request carries the token, so an <img> cannot fetch it itself. */
  regenThreadJournalImage(skillId: string, threadId: string, path: string): Promise<Blob>;
  setCurrentRegenThread(skillId: string, threadId: string): Promise<void>;
  publish(skillId: string): Promise<{ cloud_id: string | null; published_version: number | null }>;
  /**
   * The organization's shared skills — the old web's request, verbatim:
   * `visibility=organization`, the org id, and `revit_version` when given.
   */
  sharedSkills(params?: { version?: string }): Promise<CloudSkill[]>;
  /**
   * Download into the local store. `version` is the fallback year for a cloud
   * skill that does not say its own; the agent prefers the skill's.
   */
  download(cloudId: string, version: string | null): Promise<{ outcome: "created" | "updated" | "up_to_date" }>;
  removeCloud(cloudId: string): Promise<void>;
}

/** A 404 from a route an older agent does not have reads as an empty answer. */
function notFoundAs<T>(empty: T): (error: unknown) => T {
  return (error) => {
    if (error instanceof SkillsError && error.status === 404) return empty;
    throw error;
  };
}

export function createSkillsClient(auth: SkillsAuth, client: AgentClient = agentClient): SkillsClient {
  const headers = (json: boolean): Record<string, string> => ({
    Authorization: bearer(auth.token),
    "X-Organization-Id": auth.orgId,
    ...(json ? { "Content-Type": "application/json" } : {}),
  });

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await client.fetch(path, {
      method,
      headers: headers(body !== undefined),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const parsed = parseAskError(response.status, payload, response.statusText);
      throw new SkillsError(parsed, parsed.message || `HTTP ${response.status}`);
    }
    return payload as T;
  }

  const id = (s: string): string => encodeURIComponent(s);

  return {
    async list() {
      // Both generations, one list. A v1 row that claims AutoCAD is test data a dev build
      // saved before v2 existed — AutoCAD skills are v2 only — so it is not shown. An agent
      // without the v2 routes (404) just has no v2 skills.
      const [v1, v2] = await Promise.all([
        request<ApiSkill[]>("GET", "/skills"),
        request<ApiSkill[]>("GET", "/v2/skills").catch(notFoundAs<ApiSkill[]>([])),
      ]);
      const v1Rows = (Array.isArray(v1) ? v1 : []).filter((row) => skillTarget({ host: row.target, revitVersion: row.artifact?.revitVersion }).kind !== "autocad");
      return [...v1Rows, ...(Array.isArray(v2) ? v2 : [])].map(toSkill);
    },
    async get(skillId) {
      const data = await request<ApiSkill & { code?: string; skillContent?: string }>("GET", skillPath(skillId));
      return { ...toSkill(data), code: data.code ?? "", skillContent: data.skillContent ?? "" };
    },
    async folders() {
      const [v1, v2] = await Promise.all([
        request<{ folders?: SkillFolder[] }>("GET", "/skill-folders"),
        request<{ folders?: SkillFolder[] }>("GET", "/v2/skill-folders").catch(notFoundAs<{ folders?: SkillFolder[] }>({})),
      ]);
      return [...(Array.isArray(v1?.folders) ? v1.folders : []), ...(Array.isArray(v2?.folders) ? v2.folders : [])];
    },
    async createFolder(name, version, kind) {
      // An AutoCAD folder is a v2 folder: vendor-wide, no year.
      if (kind === "autocad") {
        const data = await request<{ folder: SkillFolder }>("POST", "/v2/skill-folders", { name });
        return data.folder;
      }
      // `revitVersion` is 2.x's field name for the same fact, kept on the wire.
      const data = await request<{ folder: SkillFolder }>("POST", "/skill-folders", { name, revitVersion: version });
      return data.folder;
    },
    async move(skillId, folderName) {
      await request("PATCH", skillPath(skillId, `/folder`), { folderName });
    },
    async setEnabled(skillId, enabled) {
      await request("PATCH", skillPath(skillId, `/enabled`), { enabled });
    },
    async remove(skillId) {
      await request("DELETE", skillPath(skillId));
    },
    async versions(skillId) {
      return request<SkillVersionsPage>("GET", skillPath(skillId, `/versions`));
    },
    async version(skillId, version) {
      return request<SkillVersionFiles>("GET", skillPath(skillId, `/versions/${version}`));
    },
    async executions(skillId, limit = 20) {
      const data = await request<{ items?: ExecutionSummary[] }>("GET", skillPath(skillId, `/executions?limit=${limit}`));
      return Array.isArray(data?.items) ? data.items : [];
    },
    async execution(skillId, executionId) {
      return request<ExecutionDoc>("GET", skillPath(skillId, `/executions/${id(executionId)}`));
    },
    async createSkill(body) {
      // An AutoCAD skill is created as a skill v2.
      const data = await request<{ skill?: { id?: string; artifact?: { displayName?: string; name?: string } } }>(
        "POST",
        body.vendor === "autocad" ? "/v2/skills" : "/skills",
        body,
      );
      const skill = data?.skill;
      return { id: skill?.id ?? "", displayName: skill?.artifact?.displayName || skill?.artifact?.name || body.displayName || body.name };
    },
    async update(skillId, fields) {
      await request("PUT", skillPath(skillId), fields);
    },
    async saveVersion(skillId, message) {
      return request<{ version: number }>("POST", skillPath(skillId, `/save-version`), message ? { message } : {});
    },
    async restore(skillId, version) {
      const data = await request<{ version: number; open_version?: string; versionFolders?: string[] }>("POST", skillPath(skillId, `/restore/${version}`), {});
      return {
        version: data.version,
        ...(typeof data.open_version === "string" ? { openVersion: data.open_version } : {}),
        ...(Array.isArray(data.versionFolders) ? { versionFolders: data.versionFolders } : {}),
      };
    },
    async regenThreads(skillId) {
      const data = await request<{ threads?: RegenThreadSummary[]; current?: string | null }>("GET", skillPath(skillId, `/regen-threads`));
      return { threads: Array.isArray(data?.threads) ? data.threads : [], current: data?.current ?? null };
    },
    async regenThreadJournal(skillId, threadId) {
      const data = await request<{ entries?: SkillJournalRecord[] }>("GET", skillPath(skillId, `/regen-threads/${encodeURIComponent(threadId)}/journal`));
      return Array.isArray(data?.entries) ? data.entries : [];
    },
    async regenThreadJournalImage(skillId, threadId, path) {
      const route = skillPath(skillId, `/regen-threads/${encodeURIComponent(threadId)}/journal/image`);
      const response = await client.fetch(`${route}${route.includes("?") ? "&" : "?"}${new URLSearchParams({ path })}`, { method: "GET", headers: headers(false) });
      if (!response.ok) throw new SkillsError(parseAskError(response.status, null, response.statusText), `HTTP ${response.status}`);
      return response.blob();
    },
    async setCurrentRegenThread(skillId, threadId) {
      await request("PUT", skillPath(skillId, `/regen-threads/current`), { thread_id: threadId });
    },
    async publish(skillId) {
      const data = await request<{ cloud_id?: string | null; published_version?: number | null }>("POST", skillPath(skillId, `/publish`), {});
      return { cloud_id: data?.cloud_id ?? null, published_version: data?.published_version ?? null };
    },
    async sharedSkills(params = {}) {
      const query = new URLSearchParams({ visibility: "organization", organization_id: auth.orgId });
      /**
       * **Ask for the most api-v2 will give.** `GET /skills/shared` defaults to
       * `limit=50` and caps at 200, and this call sent neither — so an organization
       * with more than 50 shared skills has been seeing the first 50 and no sign of
       * the rest. The client-side pager makes that worse, not better: it would say
       * "Page 1 of 9" over a truncated set as though that were all of them.
       *
       * 200 is the ceiling, not a fix. The response is a bare array with no total, so
       * a client cannot tell a full page from a truncated one, which is why real
       * paging needs an api-v2 change and is tracked separately.
       */
      query.set("limit", "200");
      if (params.version) query.set("revit_version", params.version);
      const data = await request<CloudSkill[]>("GET", `/skills/cloud/shared?${query.toString()}`);
      return Array.isArray(data) ? data : [];
    },
    async download(cloudId, version) {
      const data = await request<{ outcome?: "created" | "updated" | "up_to_date" }>(
        "POST",
        `/skills/sync/${id(cloudId)}`,
        version ? { revitVersion: version } : {},
      );
      return { outcome: data?.outcome ?? "updated" };
    },
    async removeCloud(cloudId) {
      await request("DELETE", `/skills/cloud/${id(cloudId)}`);
    },
    async execute(skillId, body) {
      const data = await request<{ data: Omit<ExecutionResult, "executionAt">; executionAt: string }>(
        "POST",
        skillPath(skillId, `/execute`),
        {
          target_session_id: body.targetSessionId,
          parametersJson: body.parametersJson,
          ...(body.autoTransaction !== undefined ? { autoTransaction: body.autoTransaction } : {}),
        },
      );
      return { ...data.data, executionAt: data.executionAt };
    },
  };
}

// --- AutoCAD ------------------------------------------------------------------------------

/**
 * The year the selected instance runs that this skill has no folder for, or null — the
 * editor then turns running and editing off. A cross-version build, started from a
 * supported year, is how a skill gains a year.
 */
export function missingYear(versions: readonly string[], selectedYear: string): string | null {
  return /^\d{4}$/.test(selectedYear) && !versions.includes(selectedYear) ? selectedYear : null;
}
